"use client";

import { ensureShaderFloat } from "@/mono/lib/gl";
import { mediaMobile } from "@/mono/lib/responsive";
import { easeInOutExpo } from "@/mono/lib/styles/easing";
import { setVh } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import { css } from "styled-components";

export const mobilePagePadding = "60rem";
export const desktopPagePadding = "120rem";
export const maxWidth = 2000;

export const getBasePadding = (isMobileLayout) => {
  return isMobileLayout ? mobilePagePadding : desktopPagePadding;
};

export const misc = {
  // TODO: These can all be removed
  navigationHeight: () => "var(--navigationHeight)",
  basePadding: () => "var(--basePadding)",
  pagePadding: () => "var(--pagePadding)",

  pageHalfWidth: "50%",

  borderRadius: "20%",

  gl: {
    borderRadius: ensureShaderFloat(10),
  },
};

export const colors = {
  white: "#ffffff",
  black: "#000000",

  grey: "#BFBFBF",
  greyLight1: "#D5D5D5",

  nevada: "#5C6566",
  aquaMarine: "#00FFC2",

  aquaMarineDark: "#00FFC2",
  stromboli: "#376A5D",

  iron: "#EAEAEA",
  ironDarker: "#dbd9d9",
  outerSpace: "#2B393B",
  outerSpaceLight: "#333E40",

  pastelPink: "#FFD4E5",

  tuatara: "#232323",
  mineShaft: "#262626",
  nobel: "#B7B7B7",

  outerSpaceDark: "#5A6466",
};

export const lightTheme = {
  __TYPE: "light",
  background: colors.iron,
  backgroundAccent: colors.grey,
  backgroundAccentLight: colors.greyLight1,
  textColor: colors.outerSpace,
  inactiveTextColor: colors.nevada,
  scrollTextBackground: colors.stromboli,
  accent: colors.aquaMarineDark,
  accent2: colors.pastelPink,

  menuBackground: colors.outerSpace,
  menuBackgroundText: colors.outerSpaceLight,

  projectMeta: colors.outerSpace,
  howWeDoItTextColor: colors.aquaMarineDark,
  menuText: colors.aquaMarine,
  floatingTitle: colors.aquaMarineDark,

  smallDividerColor: colors.outerSpace,

  heroGridColorSecondary: colors.outerSpaceLight,
};

export const darkTheme = {
  __TYPE: "dark",
  background: colors.outerSpace,
  backgroundAccent: colors.outerSpaceLight,
  backgroundAccentLight: colors.outerSpaceLight,
  textColor: colors.aquaMarine,
  inactiveTextColor: colors.stromboli,
  scrollTextBackground: colors.stromboli,
  secondaryTextColor: colors.iron,
  accent: colors.stromboli,
  accent2: colors.pastelPink,

  menuBackground: colors.tuatara,
  menuBackgroundText: colors.mineShaft,
  menuText: colors.nobel,

  projectMeta: colors.iron,
  howWeDoItTextColor: colors.outerSpaceDark,
  floatingTitle: colors.nevada,
  smallDividerColor: colors.nevada,

  heroGridColorSecondary: colors.outerSpaceLight,
};

export const pinkTheme = {
  __TYPE: "pink",
  background: colors.outerSpace,
  backgroundAccent: colors.grey,
  textColor: colors.aquaMarine,
  inactiveTextColor: colors.stromboli,
  scrollTextBackground: colors.stromboli,
  secondaryTextColor: colors.iron,
  accent: colors.stromboli,
  accent2: colors.pastelPink,

  menuBackground: colors.iron,
  menuBackgroundText: colors.outerSpaceLight,
};

export const theme = {
  ...lightTheme,
};

export const themesMap = {
  light: lightTheme,
  dark: darkTheme,
  pink: pinkTheme,
};

export const FontNames = {
  IBMPlexMono: "IBM Plex Mono",
  RestartSoft: "RestartSoft",
};

export const fonts = {
  face: {
    [FontNames.RestartSoft]: "'Restart Soft', sans-serif",
    [FontNames.IBMPlexMono]: "'IBM Plex Mono', sans-serif",
  },
  // Based on Material design typography scale, adjust as needed
  // fontSize: rem, letterSpacing: em
  // fontFamily: { [localeCode]: localeSpecificFont, default: defaultFont }
  scale: {
    bigTitle: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 400 },
      lineHeight: { mobile: 1 },
      fontWeight: 400,
    },
    heading1: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 220 },
      lineHeight: { mobile: 1 },
      fontWeight: 400,
    },
    heading2: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 100 },
      lineHeight: { mobile: 1 },
      fontWeight: 900,
    },

    // Scrolling text
    heading3: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 80, desktop: 100 },
      lineHeight: { mobile: 1.1, desktop: 1.2 },
      fontWeight: 500,
    },

    // Menu text
    heading4: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 48 },
      lineHeight: { mobile: 1 },
      fontWeight: 100,
    },

    heading5: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 250 },
      lineHeight: { mobile: 1 },
      fontWeight: 400,
    },

    // Section titles
    heading6: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 50 },
      lineHeight: { mobile: 1 },
      fontWeight: 300,
    },

    loaderText: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 500 },
      lineHeight: { mobile: 1 },
      fontWeight: 500,
    },

    whatWeDoMobileSlider: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 70 },

      fontWeight: 400,
    },

    paragraph: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 50, desktop: 50 },
      lineHeight: { mobile: 1.4 },
      fontWeight: 100,
    },

    paragraphStrong: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 50, desktop: 50 },
      lineHeight: { mobile: 1.4 },
      fontWeight: 500,
    },

    // Footer text
    smallParagraph: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 40 },
      lineHeight: { mobile: 1.4 },
      fontWeight: 100,
    },

    smallerParagraph: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 30 },
      lineHeight: { mobile: 1.4 },
      fontWeight: 400,
    },

    monoSmall: {
      fontFamily: { default: FontNames.IBMPlexMono },
      fontSize: { mobile: 25 },
      lineHeight: { mobile: 1.4 },
      fontWeight: 400,

      textTransform: "uppercase",
    },
    monoMedium: {
      fontFamily: { default: FontNames.IBMPlexMono },
      fontSize: { mobile: 80, desktop: 80 },
      lineHeight: { mobile: 1.4 },
      fontWeight: 400,

      textTransform: "uppercase",
    },
    li: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 75 },
      lineHeight: { mobile: 1.4 },
      fontWeight: 400,
    },

    menuLinks: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 150, desktop: 180 },
      lineHeight: { mobile: 1.2, desktop: 1.4 },
      fontWeight: 100,
    },
    menuMono: {
      fontFamily: { default: FontNames.IBMPlexMono },
      fontSize: { mobile: 50, desktop: 42 },
      lineHeight: { mobile: 1.2, desktop: 1.4 },
      fontWeight: 400,
    },

    projectNumber: {
      fontFamily: { default: FontNames.RestartSoft },
      fontWeight: 400,
      fontSize: { mobile: 160 },
    },

    floatingTitle: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 260, desktop: 300 },
      lineHeight: { mobile: 1 },
      fontWeight: 400,
    },

    projectsTitle: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 80 },
      lineHeight: { mobile: 1.2 },
      fontWeight: 500,
    },

    heroText: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 180, desktop: 340 },
      lineHeight: { mobile: 1.1, desktop: 1.2 },
      fontWeight: 500,
    },

    glassCardNumber: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 100, desktop: 110 },
      lineHeight: { mobile: 1.1, desktop: 1.2 },
      fontWeight: 500,
    },

    glassCardTitle: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 70, desktop: 60 },
      lineHeight: { mobile: 1.2 },
      fontWeight: 500,
    },

    glassCardParagraph: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 40, desktop: 36 },
      lineHeight: { mobile: 1.4 },
      fontWeight: 100,
    },

    serviceHeroTitle: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 700, desktop: 900 },
      lineHeight: { mobile: 1 },
      fontWeight: 500,
    },

    serviceHeroSubTitle: {
      fontFamily: { default: FontNames.RestartSoft },
      fontSize: { mobile: 80, desktop: 100 },
      lineHeight: { mobile: 1 },
      fontWeight: 400,
    },
  },
};

const innerWidth = css`
  padding-left: calc(${(props) => props.theme.misc.pagePadding()});
  padding-right: calc(${(props) => props.theme.misc.pagePadding()});
  margin: 0 auto;
`;

export const sectionTop = css`
  padding-top: 70rem;
`;

export const paddingBottomDesktop = "280rem";
export const paddingBottomMobile = "120rem";
export const sectionBottom = css`
  padding-bottom: ${paddingBottomDesktop};
  ${mediaMobile(`padding-bottom: ${paddingBottomMobile};`)}
`;

export const mobileHeroAspectRatio = "5/7";

export const util = {
  inner: css`
    ${innerWidth}
    ${sectionTop}
    ${sectionBottom}
  `,
  innerWidth,
  heroSizing: css`
    width: calc(100% - (${(props) => props.theme.misc.pagePadding()} * 2));
    aspect-ratio: 16/9;

    margin-left: ${(props) => props.theme.misc.pagePadding()};
    margin-bottom: ${(props) => props.theme.misc.basePadding()};
    margin-top: calc(
      ${misc.navigationHeight()} + ${(props) => props.theme.misc.basePadding()}
    );

    ${mediaMobile(css`
      aspect-ratio: ${mobileHeroAspectRatio};
      margin-bottom: calc(${(props) => props.theme.misc.basePadding()} * 2);
    `)}

    ${({ theme }) => {
      // HINT: For some reason this does get triggered on resize
      const { windowSize } = useGlobalStore.getState();

      if (theme.isMobileLayout) {
        return css`
          max-height: calc(
            ${setVh(100)} - ${misc.navigationHeight()} -
              (${(props) => props.theme.misc.basePadding()} * 2)
          );
        `;
      }

      return css`
        max-height: calc(
          ${setVh(100)} - ${misc.navigationHeight()} -
            (${(props) => props.theme.misc.basePadding()} * 2)
        );
      `;

      // Why was this again?
      // return (
      //   windowSize.width <= maxWidth &&
      //   css`
      //     max-height: calc(
      //       ${setVh(100)} - ${misc.navigationHeight()} -
      //         (${(props) => props.theme.misc.basePadding()} * 2)
      //     );
      //   `
      // );
    }}
  `,
  isUnderMaxWidth: () => {
    const { windowSize } = useGlobalStore.getState();

    return windowSize.width <= maxWidth;
  },
  iconUnderlineHover: (duration = "0.8s") => css`
    &::before {
      /* Matches icon duration */
      --duration: ${duration};
      content: "";
      display: block;
      margin-top: 2rem;
      width: 100%;
      height: 1px;
      background: ${({ theme }) => theme.theme.textColor};
      position: absolute;
      bottom: -5rem;
      left: 0;
      transform: scaleX(0);
      transform-origin: right;

      transition: transform var(--duration) ${easeInOutExpo};
    }
  `,
  iconUnderlineHoverTransition: css`
    &::before {
      transform: scaleX(1);
      transform-origin: left;
    }
  `,
};
